// Devices tab: FrenchPrep-style Device Alerts. Flagged accounts (>= 2 distinct
// mobile UAs OR desktop UAs in the last 30d), grouped by org. Expand to see
// each device fingerprint with Forget; per-account Block/Unblock.
import React, { useCallback, useEffect, useState } from 'react';
import { MonitorSmartphone, RefreshCw, ChevronDown, ChevronRight, Ban, ShieldCheck, Smartphone, Monitor, Tablet } from 'lucide-react';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

function DIcon({ device }) {
  const cls = 'h-3.5 w-3.5';
  if (device === 'mobile') return <Smartphone className={cls} />;
  if (device === 'tablet') return <Tablet className={cls} />;
  return <Monitor className={cls} />;
}

function timeAgo(iso) {
  if (!iso) return '—';
  const diff = new Date(iso).getTime() - Date.now();
  const absS = Math.floor(Math.abs(diff) / 1000);
  const chunk = (s) => {
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`;
    return `${Math.floor(s / 86400)}d`;
  };
  return diff >= 0 ? `in ${chunk(absS)}` : `${chunk(absS)} ago`;
}

export default function DevicesTab() {
  const [data, setData] = useState({ orgs: [], total_flagged: 0, total_orgs: 0 });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.adminDeviceAlerts();
      setData(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to load device alerts.'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const block = async (u) => {
    if (!window.confirm(`Block ${u.name || u.email}?\nOn their next login they will see: "Access is currently unavailable for your account. Please contact your admin."`)) return;
    try {
      await api.adminBlockUser(u.user_id, 'multi_device');
      toast.success('User blocked.');
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not block.'); }
  };

  const unblock = async (u) => {
    try {
      await api.adminUnblockUser(u.user_id);
      toast.success('User unblocked.');
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not unblock.'); }
  };

  const forget = async (ua) => {
    try {
      await api.adminForgetDevice(ua);
      toast.success('Device forgotten.');
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not forget.'); }
  };

  return (
    <section className="mt-6" data-testid="devices-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <MonitorSmartphone className="h-4 w-4 text-slate-700" />
          <h3 className="font-display text-base font-semibold text-slate-900">Device Alerts</h3>
        </div>
        <button onClick={load} disabled={loading}
          className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
          <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} /> Refresh
        </button>
      </div>
      <p className="mt-2 max-w-3xl text-sm text-slate-500">
        Accounts active on more than one mobile <b>or</b> more than one desktop in the last <b>30 days</b>.
        A second mobile or a second desktop usually means the account is being shared. Detection never blocks automatically — review and decide.
      </p>
      <p className="mt-3 text-sm text-slate-700">
        <b>{data.total_flagged}</b> flagged accounts · across <b>{data.total_orgs}</b> organisation{data.total_orgs === 1 ? '' : 's'}
      </p>

      {data.orgs.length === 0 ? (
        <p className="mt-6 rounded-lg border border-dashed border-slate-200 bg-white px-6 py-8 text-center text-sm text-slate-500">
          No device alerts. Nothing looks shared or scraped right now.
        </p>
      ) : (
        <div className="mt-4 space-y-6">
          {data.orgs.map((bucket) => (
            <div key={bucket.org_id}>
              <p className="font-display text-[15px] font-semibold text-slate-900">{bucket.org_name}</p>
              <p className="text-xs text-slate-500">{bucket.flagged_count} flagged account{bucket.flagged_count === 1 ? '' : 's'}</p>
              <div className="mt-2 space-y-2">
                {bucket.accounts.map((acct) => {
                  const key = `${bucket.org_id}-${acct.user_id}`;
                  const isOpen = expanded === key;
                  return (
                    <div key={acct.user_id} className="overflow-hidden rounded-lg border border-slate-200 bg-white" data-testid={`device-row-${acct.user_id}`}>
                      <div className="flex items-center gap-3 px-4 py-3">
                        <button type="button" onClick={() => setExpanded(isOpen ? null : key)}
                          className="grid h-8 w-8 place-items-center rounded-md text-slate-500 hover:bg-slate-100">
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="truncate font-display text-sm font-semibold text-slate-900">{acct.name || acct.email}</p>
                            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-600">{(acct.role || 'user').replace('_', ' ')}</span>
                            {acct.blocked && <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-rose-800">Blocked</span>}
                          </div>
                          <p className="text-xs text-slate-500">{acct.email}</p>
                          <div className="mt-1 flex flex-wrap items-center gap-3 text-xs">
                            {acct.mobile_count > 1 && <span className="inline-flex items-center gap-1 text-amber-800"><Smartphone className="h-3 w-3" /> {acct.mobile_count} mobile devices</span>}
                            {acct.desktop_count > 1 && <span className="inline-flex items-center gap-1 text-amber-800"><Monitor className="h-3 w-3" /> {acct.desktop_count} desktops</span>}
                            <span className="text-slate-400">Last seen {timeAgo(acct.last_at)}</span>
                          </div>
                        </div>
                        {acct.blocked ? (
                          <button onClick={() => unblock(acct)}
                            className="inline-flex items-center gap-1 rounded-md border border-emerald-300 px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50"
                            data-testid={`device-unblock-${acct.user_id}`}>
                            <ShieldCheck className="h-3.5 w-3.5" /> Unblock
                          </button>
                        ) : (
                          <button onClick={() => block(acct)}
                            className="inline-flex items-center gap-1 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-700"
                            data-testid={`device-block-${acct.user_id}`}>
                            <Ban className="h-3.5 w-3.5" /> Block
                          </button>
                        )}
                      </div>

                      {isOpen && (
                        <div className="border-t border-slate-100 bg-slate-50/50 p-3">
                          <div className="space-y-2">
                            {acct.sessions.length === 0 && <p className="text-xs text-slate-500">No sessions retained (all forgotten).</p>}
                            {acct.sessions.map((s, i) => (
                              <div key={`${s.user_agent}-${i}`} className="flex items-start gap-3 rounded border border-slate-200 bg-white px-3 py-2 text-xs">
                                <DIcon device={s.device} />
                                <div className="min-w-0 flex-1">
                                  <p className="font-mono text-[11px] uppercase text-slate-500">{s.device} · {s.os} · {s.browser}</p>
                                  <p className="mt-0.5 truncate text-[11px] text-slate-500">{s.user_agent}</p>
                                  <p className="mt-1 text-[11px] text-slate-500">IP {s.ip || '—'} · first {timeAgo(s.first_at)} · last {timeAgo(s.last_at)} · logins {s.logins}</p>
                                </div>
                                <button onClick={() => forget(s.user_agent)}
                                  className="rounded border border-slate-300 px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:border-slate-400"
                                  data-testid={`device-forget-${i}`}>
                                  Forget
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
